import React, { useEffect, useRef, useState } from 'react';
import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';

type Props = {
  value: number | null | undefined;
  onCommit: (value: number | null) => void;
  min?: number;
  ariaLabel: string;
  formatter?: InputNumberProps['formatter'];
  suffix?: React.ReactNode;
};

/** Numeric editor where a blank input is a persisted null, not "not applicable". */
const NullableInlineNumber: React.FC<Props> = ({
  value,
  onCommit,
  min,
  ariaLabel,
  formatter,
  suffix,
}) => {
  const savedValue = value ?? null;
  const [draft, setDraft] = useState<number | null>(savedValue);
  const discardNextBlur = useRef(false);

  useEffect(() => setDraft(value ?? null), [value]);

  const commit = () => {
    if (discardNextBlur.current) {
      discardNextBlur.current = false;
      return;
    }
    if (draft !== savedValue) onCommit(draft);
  };

  return (
    <span className="ie-num ie-num--plain ie-num--nullable">
      <InputNumber
        value={draft}
        min={min}
        controls
        size="small"
        placeholder="Not set"
        aria-label={ariaLabel}
        formatter={formatter}
        suffix={suffix}
        onChange={next => setDraft(next as number | null)}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
          } else if (event.key === 'Escape') {
            discardNextBlur.current = true;
            setDraft(savedValue);
            (event.target as HTMLInputElement).blur();
          }
        }}
      />
    </span>
  );
};

export default NullableInlineNumber;
